import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DeviceCategory } from "../../CableTakeoffTabV3/deviceCategories";
import type {
  KarlBoardFeeds,
  KarlFeeders,
  KarlHeights,
} from "../../CableTakeoffTabV3/CablesChatPanel/types";
import { FEEDER_CABLE_SUGGESTION, feederCircuitId } from "../../CableTakeoffTabV3/feederCircuits";
import { formatMetres } from "../../CableTakeoffTabV3/formatMetres";
import { countLiveCircuits } from "../../CableTakeoffTabV3/countLiveCircuits";
import { lumpedCircuitLimit } from "../../CableTakeoffTabV3/lumpedCircuitLimit";
import { useFirstCalculationPending } from "../../CableTakeoffTabV3/useFirstCalculationPending";
import { describeLiveTakeoff } from "../../CableTakeoffTabV3/karlLiveTakeoff";
import { sumTypesByCategory } from "../../CableTakeoffTabV3/boardFeeds";
import { useCableTakeoff } from "../useCableTakeoff";
import { boardDisplayName } from "../useCableTakeoff/utils";
import { buildCircuitKeyMap } from "../../CableTakeoffTabV3/circuitKeys";
import { buildCircuitBreakerMap } from "../../CableTakeoffTabV3/circuitBreakers";
import { buildUnfilledWays } from "../../CableTakeoffTabV3/unfilledWays";
import { useKarlFocusRelease } from "../../CableTakeoffTabV3/karlFocusRelease";
import { findCircuitOnDiagram } from "../../CableTakeoffTabV3/circuitOnDiagram";
import type { RouteStatusKind } from "../../CableTakeoffTabV3/karlRoutes";
import type { KarlAskAboutSource } from "../../CableTakeoffTabV3/useCablesChat";
import type {
  CableSourceEdit,
  KarlWallsStep,
  KarlCircuitEdit,
} from "../../CableTakeoffTabV3/CablesChatPanel";
import type { CircuitBoardFocus } from "../useCableTakeoff/types";
import type { BoardDiagramV3 } from "../../utils/types";
import type {
  UseCableTakeoffReviewArgs,
  UseCableTakeoffReviewResult,
} from "./types";

export const useCableTakeoffReview = ({
  projectId,
  language,
  cables,
  cablesPublished,
  selectedPlan,
  rightTab,
  imageScaleFactor,
  pageScale,
  byGroupId,
  iconByGroupId,
  canAccessCableTakeoffBeta,
  floorPlanPanelHandlers,
  selectedDiagram,
  diagramRecords,
  loadedDiagrams,
  loadAllDiagrams,
  selectedDiagramRecordIndex,
  selectDiagramRecord,
  selectItemType,
  selectOccurrence,
  setSidebarMode,
  setRightTab,
}: UseCableTakeoffReviewArgs): UseCableTakeoffReviewResult => {
  const { t, i18n } = useTranslation();
  // The chat panel owns the thread; it registers these for the page to call.
  const karlResetRef = useRef<(() => void) | null>(null);
  const karlNewChatRef = useRef<(() => void) | null>(null);
  const karlAskAboutRef = useRef<
    ((circuitId: string, source: KarlAskAboutSource) => void) | null
  >(null);
  // Tells a self-picked run apart from Karl's own focus — both end in focusCircuit.
  const selfPickedCircuitRef = useRef<string | null>(null);
  const karlFocusedCircuitRef = useRef<string | null>(null);

  // Karl settles routes in his panel; the circuit list beside him marks them.
  const [karlRouteStatus, setKarlRouteStatus] = useState<
    Record<string, RouteStatusKind>
  >({});
  // The run Karl is asking about; the plan and the circuit list must agree on it.
  const [karlAwaitingRunPick, setKarlAwaitingRunPick] = useState<string | null>(
    null,
  );
  const [karlRouteUnderReview, setKarlRouteUnderReview] = useState<
    string | null
  >(null);
  const [karlBoardUnderReview, setKarlBoardUnderReview] = useState<
    string | null
  >(null);
  const [karlCircuitBoard, setKarlCircuitBoard] =
    useState<CircuitBoardFocus | null>(null);
  // The board opened in the runs list. Karl's circuit step outranks it on the plan.
  const [listCircuitBoard, setListCircuitBoard] = useState<string | null>(null);
  const [cableTypeCircuits, setCableTypeCircuits] = useState<string[] | null>(
    null,
  );
  // The list opens the board Karl is on, so the menu always says where he is.
  const karlCircuitBoardLabel = karlCircuitBoard?.label ?? null;
  useEffect(() => {
    if (karlCircuitBoardLabel) setListCircuitBoard(karlCircuitBoardLabel);
  }, [karlCircuitBoardLabel]);
  // ONE open board: the list's, which Karl's step also moves. Whichever he touched last is on the plan.
  const planCircuitBoard = useMemo<CircuitBoardFocus | null>(() => {
    if (!listCircuitBoard) return karlCircuitBoard;
    return {
      label: listCircuitBoard,
      isMain:
        karlCircuitBoard?.label === listCircuitBoard
          ? karlCircuitBoard.isMain
          : undefined,
    };
  }, [karlCircuitBoard, listCircuitBoard]);
  // He can take the plan back while Karl is standing on one run — see
  // `karlFocusRelease/` for why this cannot be done by clearing Karl's own state.
  const { routeUnderReviewOnPlan, releaseKarlFocus } = useKarlFocusRelease({
    karlRouteUnderReview,
  });

  const cableTakeoff = useCableTakeoff({
    projectId,
    language,
    cables,
    cablesPublished,
    selectedPlan,
    rightTab,
    imageScaleFactor,
    pageScale,
    byGroupId,
    iconByGroupId,
    routeUnderReview: routeUnderReviewOnPlan,
    circuitBoard: planCircuitBoard,
    onlyCircuitIds: cableTypeCircuits,
  });

  // The beta gate only hides the tab and its map mode; fetching still runs underneath.
  const canSeeCableTakeoff =
    canAccessCableTakeoffBeta && cableTakeoff.cablesEnabled;
  const isCableTakeoffView = rightTab === "cabletakeoff" && canSeeCableTakeoff;

  const registerKarlReset = useCallback((reset: () => void) => {
    karlResetRef.current = reset;
  }, []);
  const resetKarlThread = useCallback(() => {
    karlResetRef.current?.();
  }, []);
  const registerKarlNewChat = useCallback((newChat: () => void) => {
    karlNewChatRef.current = newChat;
  }, []);
  const startNewKarlChat = useCallback(() => {
    karlNewChatRef.current?.();
  }, []);
  const registerKarlAskAbout = useCallback(
    (ask: (circuitId: string, source: KarlAskAboutSource) => void) => {
      karlAskAboutRef.current = ask;
    },
    [],
  );
  const askKarlAboutRoute = useCallback((circuitId: string) => {
    karlAskAboutRef.current?.(circuitId, "askAbout");
  }, []);

  // A list pick selects the run and nothing more: Karl is only asked through "Karl zu dieser Leitung fragen".
  const selectCableCircuitFromList = useCallback(
    (circuitId: string | null) => {
      // Marks the run with three pulses, so "which one is that?" is answered on the drawing.
      cableTakeoff.selectCableCircuit(circuitId);
      // "Show me all of them" has to mean ALL of them, whoever is isolating.
      // Clearing only his own selection left Karl's focus holding the plan.
      if (!circuitId) releaseKarlFocus();
      if (!circuitId || karlAwaitingRunPick) return;
      // Fit the WHOLE sheet and stand down the card's own zoom: he asked to see the run he picked, not a fragment of it.
      selfPickedCircuitRef.current = circuitId;
      floorPlanPanelHandlers.current?.zoomToFit?.();
    },
    [
      cableTakeoff,
      karlAwaitingRunPick,
      floorPlanPanelHandlers,
      releaseKarlFocus,
    ],
  );

  // One-shot: stands down exactly one move — the card's, for the run he picked himself.
  const isSelfPickedPlanMove = useCallback((): boolean => {
    if (
      !karlFocusedCircuitRef.current ||
      karlFocusedCircuitRef.current !== selfPickedCircuitRef.current
    ) {
      return false;
    }
    selfPickedCircuitRef.current = null;
    return true;
  }, []);

  // Searches EVERY loaded diagram, not just the open one — a designator lives
  // in exactly one schematic, and it may not be the one on screen.
  const karlDiagramSearch = useCallback(
    (designator: string) => {
      const candidates = [
        selectedDiagram,
        ...diagramRecords
          .map((record) => loadedDiagrams[record.id])
          .filter((diagram): diagram is BoardDiagramV3 => Boolean(diagram)),
      ];
      for (const diagram of candidates) {
        if (!diagram) continue;
        const found = findCircuitOnDiagram({ diagram, designator });
        if (found) return { found, diagram };
      }
      return null;
    },
    [selectedDiagram, diagramRecords, loadedDiagrams],
  );

  // Printed run identifiers, read by the same rule as Karl's walk so row and card agree.
  const circuitKeys = useMemo(
    () => buildCircuitKeyMap({ steps: cableTakeoff.karlSteps }),
    [cableTakeoff.karlSteps],
  );

  // Breaker names read via readRouteWhy, so the row and Karl's card show the same name.
  const circuitBreakers = useMemo(
    () => buildCircuitBreakerMap({ steps: cableTakeoff.karlSteps }),
    [cableTakeoff.karlSteps],
  );

  // The schedule's rows that became no run — what makes creating A5 a click on
  // the way the schematic already describes rather than four fields retyped.
  // Read against the ids the ROWS print, so a way already answered by a circuit
  // is never offered as missing.
  const unfilledWays = useMemo(
    () =>
      buildUnfilledWays({
        steps: cableTakeoff.karlSteps,
        takenKeys: Object.values(circuitKeys).filter(
          (key): key is string => !!key,
        ),
      }),
    [cableTakeoff.karlSteps, circuitKeys],
  );

  // Boards and cable specs read off what this plan actually has — never an option that is not on the drawing.
  const feedPointOptions = useMemo(
    () =>
      (cableTakeoff.editableTakeoff.derivedGeometry?.sources ?? []).map(
        (source) => ({
          label: source.label,
          name:
            cableTakeoff.editableTakeoff.sourceNames[source.label] ??
            boardDisplayName({
              name: source.name || source.label,
              label: source.label,
            }),
        }),
      ),
    [
      cableTakeoff.editableTakeoff.derivedGeometry,
      cableTakeoff.editableTakeoff.sourceNames,
    ],
  );
  const cableTypeOptions = useMemo(
    () =>
      [
        ...new Set(
          (cableTakeoff.selectedPlanTakeoff?.boq_items ?? [])
            .map((item) => (item.cable_type ?? "").trim())
            .filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [cableTakeoff.selectedPlanTakeoff],
  );

  // Karl's hands on the plan, in PDF-point coordinates. Keyed on the read-out
  // setter, not the whole takeoff: a fresh object per render refires the card's effects.
  const { setSelectedCableCircuit, setFocusedTrayType, setTraysShown } = cableTakeoff;
  const karlPlanControl = useMemo(
    () => ({
      // Framed with room around it — zooming tight to one marker lost him the room he was in.
      zoomTo: (position: { x: number; y: number }) => {
        if (isSelfPickedPlanMove()) return;
        floorPlanPanelHandlers.current?.zoomToElements?.({
          elements: [position],
          padding: 260,
        });
      },
      zoomToAll: (positions: { x: number; y: number }[]) => {
        if (isSelfPickedPlanMove()) return;
        if (positions.length)
          floorPlanPanelHandlers.current?.zoomToElements?.({
            elements: positions,
            padding: 60,
          });
      },
      highlight: cableTakeoff.setHighlightMarker,
      // A tray he is asked about has to be on the drawing, or he cannot tell which one it is.
      highlightTrayType: (trayType: string | null) => {
        setFocusedTrayType(trayType);
        if (trayType !== null) setTraysShown(true);
      },
      // Focus only ISOLATES the run; whether the viewport follows is decided above.
      focusCircuit: (circuitId: string | null) => {
        karlFocusedCircuitRef.current = circuitId;
        if (circuitId !== selfPickedCircuitRef.current) {
          selfPickedCircuitRef.current = null;
        }
        setSelectedCableCircuit(circuitId);
      },
    }),
    [
      cableTakeoff.setHighlightMarker,
      setSelectedCableCircuit,
      setFocusedTrayType,
      setTraysShown,
      isSelfPickedPlanMove,
      floorPlanPanelHandlers,
    ],
  );

  const karlDiagramControl = useMemo(
    () => ({
      pageOf: (designator: string) => {
        const hit = karlDiagramSearch(designator);
        return hit ? hit.found.pageIndex + 1 : null;
      },
      pageImageOf: (designator: string) => {
        const hit = karlDiagramSearch(designator);
        return hit?.found.imageUrl || null;
      },
      diagramKeyOf: (designator: string) => {
        const hit = karlDiagramSearch(designator);
        return hit?.diagram.diagram.diagramKey || null;
      },
      open: (designator: string) => {
        const hit = karlDiagramSearch(designator);
        if (!hit) return;
        setSidebarMode("diagrams");
        setRightTab("board");
        // Select the owning schematic first, or the item type below belongs to a diagram nobody is showing.
        const recordIndex = diagramRecords.findIndex(
          (record) => record.id === hit.diagram.diagram.id,
        );
        if (recordIndex >= 0 && recordIndex !== selectedDiagramRecordIndex) {
          selectDiagramRecord(recordIndex);
        }
        selectItemType(hit.found.itemTypeId);
        selectOccurrence(hit.found.occurrenceIndex);
      },
    }),
    [
      karlDiagramSearch,
      diagramRecords,
      selectedDiagramRecordIndex,
      selectDiagramRecord,
      selectItemType,
      selectOccurrence,
      setSidebarMode,
      setRightTab,
    ],
  );

  const { feedsByBoard, deviceCountsByBoard, unfedCategories } = cableTakeoff.editableTakeoff;
  const karlBoardFeeds = useMemo<KarlBoardFeeds>(() => {
    const typesByCategory = sumTypesByCategory(deviceCountsByBoard);
    const unfed: KarlBoardFeeds["unfed"] = {};
    Object.entries(unfedCategories).forEach(([category, devices]) => {
      if (devices?.length) unfed[category as DeviceCategory] = devices.length;
    });
    return { byLabel: feedsByBoard, typesByCategory, countsByBoard: deviceCountsByBoard, unfed };
  }, [feedsByBoard, deviceCountsByBoard, unfedCategories]);

  const circuitNameLabels = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(cableTakeoff.circuitNames).map(([circuitId, named]) => [circuitId, named.name]),
      ),
    [cableTakeoff.circuitNames],
  );

  // What Karl answers from: the takeoff as the app holds it now, his edits included — never only the run's file.
  const { editableTakeoff, selectedPlanTakeoff } = cableTakeoff;
  const karlLiveTakeoff = useMemo(
    () =>
      describeLiveTakeoff({
        edit: editableTakeoff,
        items: selectedPlanTakeoff?.boq_items ?? [],
        circuitName: (circuitId) => circuitNameLabels[circuitId] ?? circuitId,
        trayTypes: [...new Set(cableTakeoff.cableTrays.map((tray) => tray.type))].sort(),
      }),
    [editableTakeoff, selectedPlanTakeoff, circuitNameLabels, cableTakeoff.cableTrays],
  );
  const isFirstCalculationPending = useFirstCalculationPending({
    isSetupDone: Boolean(editableTakeoff.derivedGeometry && editableTakeoff.isSetupDone),
    routing: editableTakeoff.routing,
    openRequestCount: editableTakeoff.routeRequests.length,
  });
  // Held back until the first calculation lands, or the opening quotes 0 m.
  const karlOpeningFacts = useMemo(() => {
    if (!editableTakeoff.isSetupDone || isFirstCalculationPending) return null;
    const lumped = Object.entries(editableTakeoff.derivedGeometry?.byCircuit ?? {})
      .filter(([circuitId]) => !editableTakeoff.feederCircuits[circuitId])
      .filter(
        ([circuitId, state]) =>
          state.paths.length > lumpedCircuitLimit({ category: editableTakeoff.categoryByCircuit[circuitId] }),
      )
      .map(([circuitId, state]) => ({ name: circuitNameLabels[circuitId] ?? circuitId, deviceCount: state.paths.length }))
      .sort((a, b) => b.deviceCount - a.deviceCount);
    return {
      totalM: editableTakeoff.derivedTotalM,
      ...countLiveCircuits({ edit: editableTakeoff }),
      lumped,
      unroutedDevices: cableTakeoff.unroutedSummary.total,
    };
  }, [editableTakeoff, isFirstCalculationPending, circuitNameLabels, cableTakeoff.unroutedSummary.total]);

  const {
    heights,
    storedHeights,
    setHeight,
    heightsConfirmed,
    confirmHeights,
    trayHeightsConfirmed,
    confirmTrayHeights,
  } = cableTakeoff.editableTakeoff;
  const { cableTrays } = cableTakeoff;
  const karlHeights = useMemo<KarlHeights>(() => {
    const trayPositions: Record<string, { x: number; y: number }[]> = {};
    cableTrays.forEach((tray) => {
      (trayPositions[tray.type] ??= []).push(tray.from, tray.to);
    });
    const trayTypes = Object.keys(trayPositions).sort();
    return {
      resolved: heights,
      stored: storedHeights,
      trayTypes,
      trayPositions,
      set: setHeight,
      confirmed: heightsConfirmed,
      confirm: confirmHeights,
      traysConfirmed: trayHeightsConfirmed || trayTypes.length === 0,
      confirmTrays: confirmTrayHeights,
      format: (heightM) =>
        `${formatMetres({ value: heightM, language: i18n.language, fractionDigits: 2 })} m`,
    };
  }, [
    heights,
    storedHeights,
    cableTrays,
    setHeight,
    heightsConfirmed,
    confirmHeights,
    trayHeightsConfirmed,
    confirmTrayHeights,
    i18n.language,
  ]);

  const {
    feederCircuits,
    feederOffFloor,
    feedPointByCircuit,
    cableTypeByCircuit,
    setFeederFrom,
    resetFeedPoint,
    setFeederOffFloor,
    setCableType,
    resetCableType,
    addedCableTypes,
    sourceNames,
    derivedGeometry,
    mainBoard,
  } = cableTakeoff.editableTakeoff;
  const { startPickUpstreamFor } = cableTakeoff.cableDirectEdit;
  const upstreamGrab =
    cableTakeoff.cableDirectEdit.grabbed?.kind === "upstream_for"
      ? cableTakeoff.cableDirectEdit.grabbed.label
      : null;
  const karlFeeders = useMemo<KarlFeeders>(() => {
    const nameOf = (label: string) =>
      sourceNames[label] ?? derivedGeometry?.sources.find((source) => source.label === label)?.name ?? label;
    const mainLabel = mainBoard.kind === "none" ? null : mainBoard.label;
    const byLabel: KarlFeeders["byLabel"] = {};
    Object.entries(feederCircuits).forEach(([id, feeder]) => {
      byLabel[feeder.toLabel] = {
        fromLabel: feeder.fromLabel,
        fromName: nameOf(feeder.fromLabel),
        isStored: feedPointByCircuit[id] !== undefined,
        isOffFloor: false,
        cableType: cableTypeByCircuit[id] ?? null,
      };
    });
    // Still listed once he said "another floor", so stepping back finds the question.
    Object.keys(feederOffFloor).forEach((label) => {
      if (byLabel[label] || !mainLabel) return;
      byLabel[label] = {
        fromLabel: mainLabel,
        fromName: nameOf(mainLabel),
        isStored: false,
        isOffFloor: true,
        cableType: null,
      };
    });
    return {
      byLabel,
      suggestion: FEEDER_CABLE_SUGGESTION,
      cableTypes: [
        ...new Set([
          FEEDER_CABLE_SUGGESTION,
          ...cableTypeOptions,
          ...Object.values(cableTypeByCircuit),
          ...addedCableTypes,
        ]),
      ].sort((a, b) => a.localeCompare(b)),
      setFrom: setFeederFrom,
      clearFrom: (toLabel) => resetFeedPoint(feederCircuitId(toLabel)),
      setOffFloor: setFeederOffFloor,
      setCableType: (toLabel, cableType) =>
        cableType
          ? setCableType(feederCircuitId(toLabel), cableType)
          : resetCableType(feederCircuitId(toLabel)),
      pickUpstreamFor: startPickUpstreamFor,
      pickingUpstreamFor: upstreamGrab,
    };
  }, [
    feederCircuits,
    feederOffFloor,
    feedPointByCircuit,
    cableTypeByCircuit,
    setFeederFrom,
    resetFeedPoint,
    setFeederOffFloor,
    setCableType,
    resetCableType,
    addedCableTypes,
    sourceNames,
    derivedGeometry,
    mainBoard,
    cableTypeOptions,
    startPickUpstreamFor,
    upstreamGrab,
  ]);

  const { baseSources } = cableTakeoff.editableTakeoff;
  const karlPlanBoards = useMemo(
    () => baseSources.map((source) => ({ label: source.label, name: source.name, position: { x: source.x, y: source.y } })),
    [baseSources],
  );

  const { planWalls, cableDirectEdit } = cableTakeoff;
  const karlWalls = useMemo<KarlWallsStep | undefined>(() => {
    if (planWalls.detectedLayout.phase !== "loaded") return undefined;
    return {
      control: {
        walls: planWalls.walls,
        summary: planWalls.detectedLayout.summary,
        didSaveFail: planWalls.didSaveFail,
        isCorrecting: planWalls.isCorrecting,
        setIsCorrecting: planWalls.setIsCorrecting,
        selectedWallId: planWalls.selectedWallId,
        removeWall: (wallId) => void planWalls.removeWall(wallId),
        toggleWallKind: (wallId) => void planWalls.toggleWallKind(wallId),
        resetToDetected: () => void planWalls.resetToDetected(),
        draft: cableDirectEdit.wallDraft,
        onDraw: cableDirectEdit.startWall,
        onFinishDraw: cableDirectEdit.finishWall,
        onCancelDraw: cableDirectEdit.cancel,
      },
      confirmed: editableTakeoff.wallsConfirmed,
      confirm: editableTakeoff.confirmWalls,
      show: () => planWalls.setAreWallsShown(true),
    };
  }, [planWalls, cableDirectEdit.wallDraft, cableDirectEdit.startWall, cableDirectEdit.finishWall, cableDirectEdit.cancel, editableTakeoff.wallsConfirmed, editableTakeoff.confirmWalls]);

  // Not memoized: born as an inline prop object, so a fresh identity per render is the existing contract.
  const karlSourceEdit: CableSourceEdit = {
    planBoards: karlPlanBoards,
    placed: cableTakeoff.editableTakeoff.sourceOverrides,
    start: cableTakeoff.cableDirectEdit.startMoveSource,
    grabbedLabel:
      cableTakeoff.cableDirectEdit.grabbed?.kind === "source"
        ? cableTakeoff.cableDirectEdit.grabbed.label
        : null,
    cancel: cableTakeoff.cableDirectEdit.cancel,
    rename: cableTakeoff.editableTakeoff.renameSource,
    names: cableTakeoff.editableTakeoff.sourceNames,
    remove: cableTakeoff.editableTakeoff.removeSource,
    removeTo: cableTakeoff.editableTakeoff.removeSourceTo,
    removed: cableTakeoff.editableTakeoff.removedSources,
    restore: cableTakeoff.editableTakeoff.restoreSource,
    orphaned: cableTakeoff.editableTakeoff.orphanedDevices,
    add: cableTakeoff.cableDirectEdit.startAddSource,
    added: cableTakeoff.editableTakeoff.addedSources,
    mainBoard: cableTakeoff.editableTakeoff.mainBoardView,
    mainBoardLabel:
      cableTakeoff.editableTakeoff.mainBoard.kind === "answered"
        ? cableTakeoff.editableTakeoff.mainBoard.label
        : null,
    setMainBoard: (label: string) =>
      cableTakeoff.editableTakeoff.setMainBoard({ label, by: "acceptedSuggestion" }),
    clearMainBoard: cableTakeoff.editableTakeoff.clearMainBoard,
    pickMainBoard: (placesAs) =>
      cableTakeoff.cableDirectEdit.startPickMainBoard({
        name: t(
          placesAs === "feedIn"
            ? "smartSurveyor.cablesBeta.feedInBoardName"
            : "smartSurveyor.cablesBeta.mainBoardPlacedName",
        ),
        isFeedIn: placesAs === "feedIn",
      }),
    pickingMainBoard: cableTakeoff.cableDirectEdit.grabbed?.kind === "main_board",
    setBoardRole: cableTakeoff.editableTakeoff.setBoardRole,
    clearBoardRole: cableTakeoff.editableTakeoff.clearBoardRole,
    boardFeeds: karlBoardFeeds,
    storedBoardFeeds: cableTakeoff.editableTakeoff.storedBoardFeeds,
    setBoardFeeds: cableTakeoff.editableTakeoff.setBoardFeeds,
    clearBoardFeeds: cableTakeoff.editableTakeoff.clearBoardFeeds,
    pickFeederFor: cableTakeoff.cableDirectEdit.startPickFeederFor,
    pickingFeederFor:
      cableTakeoff.cableDirectEdit.grabbed?.kind === "feeder_for"
        ? (cableTakeoff.cableDirectEdit.grabbed.label as DeviceCategory)
        : null,
    setCategoryUnbilled: cableTakeoff.editableTakeoff.setCategoryUnbilled,
    addingName:
      cableTakeoff.cableDirectEdit.grabbed?.kind === "new_source"
        ? cableTakeoff.cableDirectEdit.grabbed.label
        : null,
    drawZone: cableTakeoff.cableDirectEdit.startZone,
    drawingZoneFor: cableTakeoff.cableDirectEdit.zoneDraft?.sourceLabel ?? null,
    finishZone: cableTakeoff.cableDirectEdit.finishZone,
    zoneCorners: cableTakeoff.cableDirectEdit.zoneDraft?.points.length ?? 0,
    zones: cableTakeoff.editableTakeoff.areaFeeds,
    boardsConfirmed: cableTakeoff.editableTakeoff.boardsConfirmed,
    heights: karlHeights,
    feeders: karlFeeders,
    confirmBoards: cableTakeoff.editableTakeoff.confirmBoards,
    reopenBoards: cableTakeoff.editableTakeoff.reopenBoards,
    selectDevices: cableTakeoff.cableDirectEdit.startDevicePick,
    isSelectingDevices: cableTakeoff.cableDirectEdit.isPicking,
    pickedDeviceCount: cableTakeoff.cableDirectEdit.pickedDevices.length,
    confirmPick: cableTakeoff.confirmDevicePick,
    deviceSelection: cableTakeoff.deviceSelection,
    clearDeviceSelection: cableTakeoff.clearDeviceSelection,
    assignSelectionTo: cableTakeoff.assignDeviceSelectionTo,
    removeDevice: cableTakeoff.editableTakeoff.removeDevice,
    removeDevices: cableTakeoff.editableTakeoff.removeDevices,
    removalGroups: cableTakeoff.editableTakeoff.removalGroups,
    removedDeviceMarkers: cableTakeoff.editableTakeoff.removedDeviceMarkers,
    restoreGroup: cableTakeoff.editableTakeoff.restoreGroup,
    closeRemovalGroup: cableTakeoff.editableTakeoff.closeRemovalGroup,
    parkDevices: cableTakeoff.editableTakeoff.parkDevices,
    editRouteShape: (circuitId: string) => {
      // The plan draws only the run under edit, so the selection follows the mode into it.
      cableTakeoff.setSelectedCableCircuit(circuitId);
      cableTakeoff.cableDirectEdit.startShapeEdit(circuitId);
    },
    activeTool: cableTakeoff.cableDirectEdit.activeTool,
    shapeControls: {
      exitCount:
        cableTakeoff.cableDirectEdit.shapeExit?.destinationIds.length ?? 0,
      confirmExit: cableTakeoff.cableDirectEdit.confirmShapeExit,
      cancelExit: cableTakeoff.cableDirectEdit.cancelShapeExit,
      // Devices the edit cut off hold the mode open until he answers about them.
      end: cableTakeoff.cableDirectEdit.endShapeEdit,
      cutCount: cableTakeoff.cableDirectEdit.cutDeviceCount,
    },
    assignGroupTo: cableTakeoff.editableTakeoff.assignGroupTo,
    selectedCircuit: cableTakeoff.selectedCableCircuit,
  };

  const karlCircuitEdit: KarlCircuitEdit = {
    feedingBoardByCircuit: cableTakeoff.editableTakeoff.feedingBoardByCircuit,
    devicesByCircuit: cableTakeoff.devicesByCircuit,
    circuitLabels: circuitNameLabels,
    checked: cableTakeoff.editableTakeoff.circuitBoardsChecked,
    check: cableTakeoff.editableTakeoff.checkCircuitBoard,
    uncheck: cableTakeoff.editableTakeoff.uncheckCircuitBoard,
    suspects: cableTakeoff.boardSuspects,
    unroutedCount: cableTakeoff.boardUnroutedCount,
    // Karl's circuit step is the assignment mode narrowed to the board he has open: one way to put devices on runs.
    startPick: () => cableTakeoff.assignMode.enter(undefined, { showsPanel: false }),
    stopPick: cableTakeoff.assignMode.exit,
    pickedIds: cableTakeoff.assignMode.selectedKeys,
    moveDevices: (_destinationIds, circuitId) => {
      cableTakeoff.assignMode.assignTo({ kind: "circuit", circuitId });
    },
  };

  // Karl needs every schematic from the moment the tab opens — two requests buy every route a page reference.
  useEffect(() => {
    if (!isCableTakeoffView) return;
    loadAllDiagrams();
  }, [isCableTakeoffView, loadAllDiagrams]);

  return {
    cableTakeoff,
    canSeeCableTakeoff,
    isCableTakeoffView,
    karlRouteStatus,
    setKarlRouteStatus,
    karlRouteUnderReview,
    setKarlRouteUnderReview,
    karlBoardUnderReview,
    setKarlBoardUnderReview,
    karlCircuitBoard,
    setKarlCircuitBoard,
    karlCircuitEdit,
    karlLiveTakeoff,
    karlOpeningFacts,
    listCircuitBoard,
    setListCircuitBoard,
    setCableTypeCircuits,
    routeUnderReviewOnPlan,
    karlAwaitingRunPick,
    setKarlAwaitingRunPick,
    registerKarlReset,
    resetKarlThread,
    registerKarlNewChat,
    startNewKarlChat,
    registerKarlAskAbout,
    askKarlAboutRoute,
    selectCableCircuitFromList,
    circuitKeys,
    circuitBreakers,
    unfilledWays,
    feedPointOptions,
    cableTypeOptions,
    karlPlanControl,
    karlDiagramControl,
    karlSourceEdit,
    karlWalls,
  };
};
